// The World Series schedule for the home page, shared by the hero's countdown
// stage (components/hero-next.jsx) and the "Greg's booth" section
// (components/booth.jsx): one poll of /api/booth/series a minute for the whole
// page (slower in a background tab), plus the words each state is told in.
// No booth API (the plain Vite dev server) means nothing to show, same as the
// live picture.

import { useEffect, useState } from "react";

const POLL_MS = 60_000;
let series = null;
let timer = 0;
let busy = false;
let listening = false;
const subs = new Set();

async function tick() {
  clearTimeout(timer);
  timer = 0;
  busy = true;
  try {
    const r = await fetch("/api/booth/series", { cache: "no-store" });
    const j = r.ok ? await r.json() : null;
    series = j?.ok ? j : null;
  } catch {
    series = null;
  }
  busy = false;
  for (const fn of subs) fn(series);
  if (subs.size) timer = setTimeout(tick, document.hidden ? POLL_MS * 3 : POLL_MS);
}

// Back from another tab or the Back button: catch up now.
function listen() {
  if (listening || typeof document === "undefined") return;
  listening = true;
  const back = () => {
    if (document.visibilityState === "visible" && subs.size && !busy) tick();
  };
  document.addEventListener("visibilitychange", back);
  window.addEventListener("pageshow", back);
}

export function useSeries() {
  const [s, set] = useState(series);
  useEffect(() => {
    listen();
    subs.add(set);
    set(series);
    if (!busy && !timer) tick();
    return () => {
      subs.delete(set);
      if (!subs.size) {
        clearTimeout(timer);
        timer = 0;
      }
    };
  }, []);
  return s;
}

// A clock that re-renders every `ms`.
export function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

// What the page is looking at right now.
//   none  - no series to show (no data, or nothing on the schedule)
//   on    - a game is being played
//   next  - counting down to `game` (`last` is the most recent final)
//   over  - the series is decided
export function seriesView(data, now = Date.now()) {
  const s = data?.series;
  const games = data?.games || [];
  if (!s || !games.length) return { phase: "none", games: [] };
  const base = { series: s, games, clubs: s.clubs };
  if (s.over)
    return { ...base, phase: "over", last: games.find((g) => g.pk === data.last) || null };
  const on = games.find((g) => g.pk === data.now);
  if (on) return { ...base, phase: "on", game: on };
  const next = games.find((g) => g.pk === data.next);
  if (!next) return { phase: "none", games };
  const last = games.find((g) => g.pk === data.last) || null;
  return { ...base, phase: "next", game: next, last, ms: Date.parse(next.at) - now };
}

const two = (n) => String(n).padStart(2, "0");

// Days, hours, minutes, seconds left; never negative.
export function clockParts(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return {
    d: Math.floor(s / 86400),
    h: two(Math.floor((s % 86400) / 3600)),
    m: two(Math.floor((s % 3600) / 60)),
    s: two(s % 60),
  };
}

export const ET = "America/New_York";

const fmt = (at, opts, tz) => {
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { ...opts, ...(tz ? { timeZone: tz } : {}) }).format(d);
};

// "8:00 PM ET", held together so a line never breaks inside it.
export const timeET = (at) =>
  `${fmt(at, { hour: "numeric", minute: "2-digit" }, ET).replace(" ", "\u00a0")}\u00a0ET`;
// "Fri, Oct 23"
export const dayET = (at) => fmt(at, { weekday: "short", month: "short", day: "numeric" }, ET);
// "Friday"
export const weekdayET = (at) => fmt(at, { weekday: "long" }, ET);
export const dateKeyET = (at) => fmt(at, { year: "numeric", month: "2-digit", day: "2-digit" }, ET);

// "7:00 PM CDT" when the reader's clock isn't on Eastern time, else "".
export function timeLocal(at) {
  let zone = "";
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {}
  if (!zone || zone === ET) return "";
  const local = fmt(at, { hour: "numeric", minute: "2-digit" });
  if (local === fmt(at, { hour: "numeric", minute: "2-digit" }, ET)) return "";
  return `${local} ${fmt(at, { timeZoneName: "short" }).split(" ").pop()}`;
}

// "Today", "Tomorrow", "Friday" or "Fri, Oct 23" for a game, seen from `now`.
export function dayWords(at, now = Date.now()) {
  const key = dateKeyET(at);
  if (key === dateKeyET(now)) return "Today";
  if (key === dateKeyET(now + 86400000)) return "Tomorrow";
  const days = (Date.parse(at) - now) / 86400000;
  return days < 6 ? weekdayET(at) : dayET(at);
}

const clubOf = (clubs, id) => clubs.find((c) => c.id === id);

// "Series tied 1-1", "Brewers lead 2-1", "Brewers win it 4-2"; "" before Game 1.
export function standingWords(view) {
  const [a, b] = view.clubs || [];
  if (!a || !b || a.wins + b.wins === 0) return "";
  if (a.wins === b.wins) return `Series tied ${a.wins}-${b.wins}`;
  const [lead, trail] = a.wins > b.wins ? [a, b] : [b, a];
  const verb = view.series?.over ? "win it" : "lead";
  return `${lead.club} ${verb} ${lead.wins}-${trail.wins}`;
}

// "Brewers 4, Rays 2" for a final.
export function scoreWords(g) {
  if (!g || g.away.score == null || g.home.score == null) return "";
  const [w, l] = g.away.score > g.home.score ? [g.away, g.home] : [g.home, g.away];
  return `${w.club} ${w.score}, ${l.club} ${l.score}`;
}

export const winnerOf = (view, g) => (g?.winner ? clubOf(view.clubs, g.winner) : null);
